import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-[#00E5FF]/60";

const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) {
      setError("A nova senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    if (next !== confirm) {
      setError("A confirmação não confere com a nova senha.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      toast.success("Senha redefinida com sucesso. Faça login com a nova senha.");
      navigate("/admin/login");
    } catch (err) {
      setError(formatApiError(err, "Link inválido ou expirado. Solicite um novo."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-6">
      <div className="bg-grid absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_70%)]" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-[#5B8CFF]/10 blur-[130px]" />

      <div className="gradient-border relative w-full max-w-md rounded-3xl p-8 sm:p-10" data-testid="admin-reset-card">
        <div className="flex items-center gap-3">
          <span className="relative block h-10 w-10 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] p-[1.5px]">
            <span className="flex h-full w-full items-center justify-center rounded-[7px] bg-[#050508]">
              <span className="text-gradient font-display text-xl font-black">E</span>
            </span>
          </span>
          <div className="leading-none">
            <span className="block font-display text-base font-bold tracking-[0.18em]">EDALIX</span>
            <span className="mt-1 block text-[9px] font-medium tracking-[0.42em] text-white/50">
              PAINEL ADMINISTRATIVO
            </span>
          </div>
        </div>

        {token ? (
          <>
            <h1 className="mt-8 font-display text-2xl font-bold tracking-tight">Definir nova senha</h1>
            <p className="mt-2 text-sm font-light text-white/55">
              Escolha uma nova senha para o painel. Mínimo de 8 caracteres.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <span className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                  NOVA SENHA
                </span>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Mínimo de 8 caracteres"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  data-testid="admin-reset-new-input"
                  className={inputClass}
                />
              </div>
              <div>
                <span className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                  CONFIRMAR NOVA SENHA
                </span>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Repita a nova senha"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="admin-reset-confirm-input"
                  className={inputClass}
                />
              </div>
              {error && (
                <p data-testid="admin-reset-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-reset-submit-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-7 py-3.5 text-sm font-semibold text-white transition-[filter,opacity] duration-300 hover:brightness-110 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                {loading ? "Salvando..." : "Salvar nova senha"}
              </button>
            </form>
          </>
        ) : (
          <div className="mt-8" data-testid="admin-reset-invalid">
            <h1 className="font-display text-2xl font-bold tracking-tight">Link inválido</h1>
            <p className="mt-2 text-sm font-light text-white/55">
              Este link de redefinição é inválido ou está incompleto. Solicite um novo na tela de login.
            </p>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/admin/login"
            data-testid="admin-reset-back-link"
            className="inline-flex items-center gap-2 text-xs font-medium text-white/45 transition-colors duration-300 hover:text-[#00E5FF]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminResetPassword;
